import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Group, Radio, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { Compass } from 'lucide-react';
import { Button, Card, EmptyState, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { TierBadge } from '../components/journey/TierBadge';
import { skillLabel } from '../components/journey/tier';
import { usePlacementAssessment, useSubmitAttempt } from '../hooks/useJourney';
import type { PlacementResponses } from '../api/types';

export const Route = createFileRoute('/journey/placement')({
  component: PlacementQuiz,
});

/**
 * Placement quiz — a new hire answers the (answer-key-stripped) built-in
 * question bank. Submitting grades server-side and raises the per-skill
 * highest-unlocked tier (placement only ever raises, never demotes), then
 * routes to the result screen. Onboarding is non-blocking: a trainee can skip
 * this and stay at the T1 floor.
 */
export function PlacementQuiz() {
  const navigate = useNavigate();
  const { data, isPending, isError } = usePlacementAssessment();
  const submit = useSubmitAttempt();
  const [selected, setSelected] = useState<Record<string, string>>({});

  const questions = useMemo(
    () => [...(data?.questions ?? [])].sort((a, b) => a.orderIndex - b.orderIndex),
    [data],
  );
  const allAnswered = questions.length > 0 && questions.every((q) => selected[q.id]);

  function onSubmit() {
    const responses: Record<string, string[]> = {};
    for (const q of questions) {
      const opt = selected[q.id];
      if (opt) responses[q.id] = [opt];
    }
    submit.mutate(
      { responses: responses as PlacementResponses },
      { onSuccess: () => navigate({ to: '/journey/result' }) },
    );
  }

  return (
    <PageContainer size="narrow">
      <PageHeader
        icon={Compass}
        title="Skill Placement"
        description="Answer a few questions so Forge can tailor scenarios to your level."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Placement' }]}
      />

      <Section
        title="Assessment"
        description="Optional — you can come back later. Placement only ever raises a tier, it never lowers one."
      >
        {isPending ? (
          <Skeleton lines={4} height={32} gap={12} />
        ) : isError ? (
          <Card variant="danger">
            <Text c="red" size="sm">
              Failed to load the placement quiz. Try refreshing the page.
            </Text>
          </Card>
        ) : questions.length === 0 ? (
          <EmptyState
            illustration={<Compass size={48} />}
            title="No questions available"
            description="There are no placement questions available right now. You can start at the T1 floor and come back later."
            cta={{ label: 'Back to Forge', onClick: () => navigate({ to: '/' }) }}
          />
        ) : (
          <Stack gap="md">
            <motion.div variants={staggerContainer} initial="hidden" animate="show">
              <Stack gap="md">
                {questions.map((q, idx) => (
                  <motion.div key={q.id} variants={cardItem}>
                    <Card>
                      <Stack gap="sm">
                        <Group gap="xs" justify="space-between" align="flex-start">
                          <Text fw={600} size="sm">
                            {idx + 1}. {q.prompt}
                          </Text>
                          <Group gap={6}>
                            <TierBadge level={1} compact size="xs" />
                          </Group>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {skillLabel(q.skillSlug)}
                        </Text>
                        <Radio.Group
                          value={selected[q.id] ?? ''}
                          onChange={(val) => setSelected((s) => ({ ...s, [q.id]: val }))}
                        >
                          <Stack gap={6} mt={4}>
                            {q.options.map((o) => (
                              <Radio key={o.id} value={o.id} label={o.text} color="green" />
                            ))}
                          </Stack>
                        </Radio.Group>
                      </Stack>
                    </Card>
                  </motion.div>
                ))}
              </Stack>
            </motion.div>

            <Group justify="flex-end">
              <Button
                variant="primary"
                disabled={!allAnswered}
                loading={submit.isPending}
                onClick={onSubmit}
              >
                Submit placement
              </Button>
            </Group>
          </Stack>
        )}
      </Section>
    </PageContainer>
  );
}
